import { Box, Button, Stack, Typography, styled } from '@mui/material';
import { InfoCircle, WarningTriangle } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';

export interface Props {
  onClearFilters: () => void;
}

const SponsorDashboardInsufficientDataState = ({ onClearFilters }: Props) => (
  <InsufficientDataCenteredWrapper>
    <InsufficientDataSection>
      <InsufficientDataBlock>
        <InsufficientIconCircle>
          <WarningTriangle width={40} height={40} />
        </InsufficientIconCircle>
        <InsufficientTitle>Insufficient Data</InsufficientTitle>
        <InsufficientDescription>
          This query returned <strong>fewer than 11 patients</strong>. To
          protect patient privacy, we cannot show breakdowns for cohorts this
          small.
        </InsufficientDescription>
        <InsufficientSubtext>Try changing some filters.</InsufficientSubtext>
        <ClearFiltersPill variant="text" onClick={onClearFilters}>
          Clear All Filters
        </ClearFiltersPill>
      </InsufficientDataBlock>
      <WhyHiddenCard>
        <WhyHiddenHeader>
          <WhyHiddenIcon>
            <InfoCircle width={24} height={24} />
          </WhyHiddenIcon>
          Why is data hidden?
        </WhyHiddenHeader>
        <WhyHiddenBody>
          Showing results for very small cohorts could potentially allow
          re-identification of individual patients. This privacy measure ensures
          HIPAA compliance.
        </WhyHiddenBody>
      </WhyHiddenCard>
    </InsufficientDataSection>
  </InsufficientDataCenteredWrapper>
);

export default SponsorDashboardInsufficientDataState;

const InsufficientDataCenteredWrapper = styled(Stack)(() => ({
  flex: 1,
  minHeight: `calc(100vh - ${theme.tokens.layout.headerHeight}px - ${theme.spacing(40)})`,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
}));

const InsufficientDataSection = styled(Stack)(() => ({
  gap: theme.spacing(5),
  alignItems: 'center',
  maxWidth: 560,
  margin: '0 auto',
}));

const InsufficientDataBlock = styled(Stack)(() => ({
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(3),
}));

const InsufficientIconCircle = styled(Stack)(() => ({
  width: 96,
  height: 96,
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.insufficientDataIconBg,
  color: theme.palette.custom.insufficientDataIconColor,
  alignItems: 'center',
  justifyContent: 'center',
}));

const InsufficientTitle = styled(Typography)(() => ({
  fontSize: 32,
  fontWeight: 600,
  color: theme.palette.custom.darkBrown,
}));

const InsufficientDescription = styled(Typography)(() => ({
  fontSize: 18,
  color: theme.palette.custom.mediumBrown,
  lineHeight: 1.6,
  '& strong': {
    fontWeight: 700,
  },
}));

const InsufficientSubtext = styled(Typography)(() => ({
  fontSize: 18,
  color: theme.palette.custom.mediumBrown,
  lineHeight: 1.6,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const ClearFiltersPill = styled(Button)(() => ({
  borderRadius: 9999,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 16,
  padding: theme.spacing(2, 6),
  backgroundColor: theme.palette.custom.darkBrown,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.custom.darkBrown,
    opacity: 0.9,
  },
  marginBottom: theme.spacing(4),
}));

const WhyHiddenCard = styled(Box)(() => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  borderRadius: 12,
  padding: theme.spacing(4, 2, 4, 6),
  textAlign: 'left',
}));

const WhyHiddenHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
  marginBottom: theme.spacing(4),
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const WhyHiddenIcon = styled(Stack)(() => ({
  color: theme.palette.custom.darkBrown,
  alignItems: 'center',
}));

const WhyHiddenBody = styled(Typography)(() => ({
  fontSize: 16,
  color: theme.palette.custom.mediumBrown,
  lineHeight: 1.6,
}));
